import React from 'react';
import ReactDOM from 'react-dom/client';
import { Statistic } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { AuthContextProvider } from './contexts/authProvider';
import { SettingContextProvider } from './contexts/settingProvider';
import { openNotification, sessionClear } from './utils/functionTool';
import axios from 'axios';
import 'antd/dist/reset.css';
import './css/var.css';
import './css/main.css';
import './css/dark.css';
import './css/rwd.css';

const root = ReactDOM.createRoot(document.getElementById('root'));
const queryClient = new QueryClient();
let abortControllers = new Map();
let isLoggedOut = false;
const { Countdown } = Statistic;

const logoutUser = () => {
  if (isLoggedOut) return;

  isLoggedOut = true;
  openNotification(
    'top',
    <div>
      登入逾時，系統將於
      <Countdown
        value={Date.now() + 3 * 1000}
        format="s"
        onFinish={() => {
          // 清除localStorage
          sessionClear();
          // 重置登出標記
          isLoggedOut = false;
          // 跳轉到登入頁面
          window.location.href = '/login';
        }}
        style={{ display: 'inline-block', padding: '0 2px' }}
        className="logout-count-down"
      />
      秒後自動登出，請再次登入
    </div>,
    '401',
  );

  // 使用AbortController取消所有進行中的request
  abortControllers.forEach((controller, url) => {
    controller.abort();
    abortControllers.delete(url);
  });

  // 重置 AbortController Map
  abortControllers.clear();
};

axios.interceptors.response.use(
  (response) => {
    const errorMessage = response?.data?.errors?.[0]?.message;
    if (errorMessage) {
      console.warn(errorMessage);

      // 檢查是否為授權相關的 GraphQL 錯誤
      if (
        errorMessage.includes('Not Authorized') ||
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('Token expired') ||
        errorMessage.includes('Invalid token')
      ) {
        console.warn('GraphQL 授權錯誤，觸發登出');
        logoutUser();
        return response;
      }

      openNotification('top', errorMessage, errorMessage);
    }
    abortControllers.delete(response.config.url);
    return response;
  },
  (error) => {
    abortControllers.delete(error.config?.url);
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // GET /report/:id 的 401 是「報告密碼錯誤」，不觸發登出
          if (error.config?.method === 'get' && /\/report\/[^/]+/.test(error.config?.url || '')) {
            break;
          }
          console.warn('未經授權');
          logoutUser();
          break;
        case 403:
          console.warn('沒有存取權限');
          openNotification('top', '沒有存取權限，無權限使用此動作，如有疑問請通知系統管理員確認權限', '403');
          logoutUser();
          break;
        case 404:
          console.warn('找不到該頁面');
          break;
        case 500:
          console.warn('伺服器出錯');
          openNotification('top', '伺服器出錯', '500');
          break;
        case 502:
          console.warn('Bad Gateway');
          openNotification('top', '伺服器無法回應', '502');
          break;
        case 503:
          console.warn('服務失效');
          openNotification('top', '服務失效', '503');
          break;
        default:
          console.warn(`連接錯誤: ${error.response.status}`);
      }
    }
    return Promise.reject(error);
  },
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthContextProvider>
        <SettingContextProvider>
          <App />
        </SettingContextProvider>
      </AuthContextProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
